/**
 * What each agent kind is CALLED, in one place.
 *
 * Two vocabularies reach this layer and they do not match, which is the whole reason
 * this file exists rather than a `.toUpperCase()` at each call site:
 *
 *   /agent/cli_status   claude · cursor · codex · agy      what you can launch
 *   /agent/idle_status  claude-code · cursor · codex · agy  what is running
 *
 * And neither is what a person calls the thing: `agy` is the binary, Antigravity is
 * the product. Capitalising the key — which is why `claude` reads correctly as
 * Claude — puts "Agy" on screen, accurate about the executable and unrecognisable as
 * what it launches. So the wire keeps its names and the reader gets theirs.
 *
 * Unknown kinds return null rather than a guess. A session running something we
 * cannot name is a real state (a CLI shipped after this build, a plain shell), and
 * inventing a label for it would make the rail assert something it does not know.
 */

/** Product names, keyed by the launch identifier from `/agent/cli_status`. */
const PRODUCTS: Record<string, string> = {
  claude: 'Claude',
  cursor: 'Cursor',
  codex: 'Codex',
  agy: 'Antigravity',
  grok: 'Grok',
  hermes: 'Hermes',
  muse: 'Muse Code',
}

/**
 * Running kinds from `/agent/idle_status` → the launch identifier above.
 * Only `claude-code` differs; the rest are already the same word.
 */
const KIND_TO_CLI: Record<string, string> = {
  'claude-code': 'claude',
  cursor: 'cursor',
  codex: 'codex',
  agy: 'agy',
  grok: 'grok',
  hermes: 'hermes',
  muse: 'muse',
}

/** `claude` → `Claude`, `agy` → `Antigravity`. For the launcher's chips. */
export function productForCli(cli: string): string {
  return PRODUCTS[cli] ?? cli
}

/**
 * `claude-code` → `Claude`, `agy` → `Antigravity`, anything else → null.
 *
 * Null is the honest answer for a kind this build has never heard of, and the caller
 * is expected to render nothing rather than a placeholder.
 */
export function productForKind(kind: string | null | undefined): string | null {
  if (!kind) return null
  const cli = KIND_TO_CLI[kind]
  return cli ? (PRODUCTS[cli] ?? null) : null
}

/** Stable slug for styling, so colours live in CSS rather than in a JS lookup. */
export function kindSlug(kind: string | null | undefined): string | null {
  if (!kind) return null
  return KIND_TO_CLI[kind] ?? null
}
